import { useCallback, useEffect, useMemo, useState } from 'react';
import { SPOTIFY_CANVAS_ERROR_CODES, type SpotifyCanvasSongResponse } from 'fym-shared/src/music24/spotifyCanvas';
import { useGetSpotifyCanvasQuery } from '../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { canvasErrorCode } from './spotifyCanvasErrors';
import { canvasRowWaits } from './spotifyCanvasRowRules';

/** The poll while the socket is down and the row waits (design spotify-canvas a2 note "Refresh"). */
export const CANVAS_POLL_MS = 10_000;

export interface SpotifyCanvasState {
  /** The answer, or null when the row does not show. */
  data: SpotifyCanvasSongResponse | null;
  refetch: () => void;
  /** A POST answered 403 M24_AUTO_UPLOAD_NOT_ENABLED: the row goes. */
  hide: () => void;
}

/**
 * The data of the row "Spotify Canvas" (design spotify-canvas a1). The row shows only with
 * `canUseAutoUpload` and an answer with `available` true. A 403 M24_AUTO_UPLOAD_NOT_ENABLED or any
 * other refused read hides it. The events `asset.publish.updated` and `artistPortal.synced` read
 * it again (tag `SpotifyCanvas`); it polls only while the socket is down and the row waits.
 */
export function useSpotifyCanvas({
  accountId,
  catalogItemUuid,
}: {
  accountId: number;
  catalogItemUuid: string;
}): SpotifyCanvasState {
  const { canUseAutoUpload } = useMusic24Capabilities();
  const [hidden, setHidden] = useState(false);
  const [waits, setWaits] = useState(false);
  const pollingInterval = useRealtimePollInterval(waits ? CANVAS_POLL_MS : 0);
  const query = useGetSpotifyCanvasQuery(
    { accountId, catalogItemUuid },
    { skip: !canUseAutoUpload || hidden, pollingInterval }
  );
  const { data, error, refetch: refetchQuery } = query;
  useEffect(() => setWaits(canvasRowWaits(data)), [data]);

  const notEnabled = error !== undefined && canvasErrorCode(error) === SPOTIFY_CANVAS_ERROR_CODES.autoUploadNotEnabled;
  const shown =
    canUseAutoUpload && !hidden && !notEnabled && error === undefined && data !== undefined && data.available;
  const refetch = useCallback(() => {
    refetchQuery().catch(console.error);
  }, [refetchQuery]);
  const hide = useCallback(() => setHidden(true), []);

  return useMemo(() => ({ data: shown ? data : null, refetch, hide }), [data, hide, refetch, shown]);
}
